import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, IonInput, LoadingController, AlertController, Platform } from '@ionic/angular/lazy';
import { Router } from '@angular/router';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ObserverService } from 'src/app/services/observer.service';
import { CountryPickerPage } from 'src/app/modals/country-picker/country-picker.page';
import { CountryService } from 'src/app/services/country.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-person-shipping-address-add',
    templateUrl: './person-shipping-address-add.page.html',
    styleUrls: ['./person-shipping-address-add.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PersonShippingAddressAddPage implements OnInit {

    @ViewChild('first_name_input', { static: false }) first_name_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    modal_css_class = '';
    @Input() person_id = null;
    person = null;
    params = {};
    countries = null;
    country = null;
    subdivisions = null;
    address_requires_subdivision = false;
    address_subdivision_label = "State / province / region / county";
    address_subdivision_label_changed = false;
    address_requires_postal_code = false;
    address_postal_code_label = "Postcode";

    // modal flags
    country_picker_modal_open = false;

    constructor(
        private modalController: ModalController,
        private alertController: AlertController,
        private loadingController: LoadingController,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService,
        private platform: Platform,
        private countryService: CountryService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        if(this.person_id) {


            this.params["first_name"] = "";
            this.params["last_name"] = "";
            this.params["organisation_title"] = "";
            this.params["premises"] = "";
            this.params["line_1"] = "";
            this.params["line_2"] = "";
            this.params["locality"] = "";
            this.params["subdivision"] = "";
            this.params["postal_code"] = "";
            this.params["country_id"] = "";
            this.params["what3words_address"] = "";
            this.params["courier_instructions"] = "";
            this.params["mobile_phone_number"] = "";

            this.loadData().then(() => { }).catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|person_shipping_address_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => {} ).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to add a shipping address, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|person_shipping_address_add_modal");
    }

    ionViewDidEnter() {
        // NOTE: we use a timeout here so the DOM gets a chance to load before manipulating elements
        setTimeout(() => {
            if(this.first_name_input) {
                this.first_name_input.setFocus();
            }
        }, 200);
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading person").then(() => {
                this.loadPerson().then(() => {
                    this.loading_spinner.message = "Loading countries";
                    this.loadCountries().then(() => {
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadPerson() {
        return new Promise<void>((resolve, reject) => {
            this.person = null;
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPerson(this.person_id).then((person) => {
                    if(person) {
                        this.person = person;
                    }
                    resolve();
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to retrieve a person.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view a person.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve a person at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadCountries() {
        return new Promise<void>((resolve, reject) => {
            this.countries = null;
            this.countryService.getCountries().then((countries) => {
                if(countries) {
                    this.countries = countries;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadCountry(country_id) {
        return new Promise<void>((resolve, reject) => {
            this.country = null;
            this.subdivisions = null;
            this.countryService.getCountry(country_id).then(async (country: any) => {
                this.country = country;
                this.loadCountrySubdivisions(country_id).then((subdivisions) => {
                    if(subdivisions) {
                        this.subdivisions = subdivisions;
                    }
                    resolve();
                })
                .catch(() => {
                    reject();
                });
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadCountrySubdivisions(country_id) {
        return new Promise((resolve, reject) => {
            this.countryService.getCountrySubdivisions(country_id).then((subdivisions) => {
                if(subdivisions) {
                    resolve(subdivisions)
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    countryChanged(){

        this.address_subdivision_label = "State / province / region / county";
        this.address_requires_subdivision = false;
        this.address_postal_code_label = "Postcode";
        this.address_requires_postal_code = false;

        if(this.params['country_id']) {

            if(this.country.subdivision_name) {
                this.address_subdivision_label = this.country.subdivision_name;
            }

            if(this.subdivisions && this.subdivisions.length != 0) {
                this.address_requires_subdivision = true;
            }

            if(this.country.postal_code_name) {
                this.address_postal_code_label = this.country.postal_code_name;
            }

            if(this.country.postal_code_required == true) {
                this.address_requires_postal_code = true;
            }

        }

        // NOTE: address_subdivision_label_changed is used to force UI to render correctly
        this.address_subdivision_label_changed = false;

        setTimeout(() => {
            this.address_subdivision_label_changed = true;
        }, 0);

    }

    close() {
        this.modalController.dismiss();
    }

    async presentCountryPickerModal() {
        if(!this.country_picker_modal_open) {
            this.country_picker_modal_open = true;
            let existing_country_id = "";
            if(this.params['country_id']) {
                existing_country_id = this.params['country_id'];
            }
            const modal = await this.modalController.create({
                component: CountryPickerPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    country_id: (this.params['country_id'] ? this.params['country_id'] : "")

                }
            });
            modal.onDidDismiss().then((data) => {
                this.country_picker_modal_open = false;
                if(data.data) {
                    if(data.data == "country_deleted") {
                        delete this.params['premises'];
                        delete this.params['line_1'];
                        delete this.params['line_2'];
                        delete this.params['locality'];
                        delete this.params['subdivision'];
                        delete this.params['postal_code'];
                        delete this.params['country_id'];
                        this.country = null;
                        this.countryChanged();
                    }
                    else {
                        if(data.data['country_id'] != existing_country_id) {
                            this.params["country_id"] = data.data['country_id'];
                            this.loadCountry(data.data['country_id']).then(() => {
                                this.countryChanged();
                            })
                            .catch(() => { });
                        }
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async add() {

        let validated = true;
        let parsed_what3words_address = null;

        if(this.params['what3words_address'] != null) {

            parsed_what3words_address = this.params['what3words_address'].trim();

            if(parsed_what3words_address.startsWith("///")) {
                parsed_what3words_address = parsed_what3words_address.substring(3);
            }
            else if(parsed_what3words_address.toLowerCase().indexOf("what3words.com/") !== -1) {
                parsed_what3words_address = parsed_what3words_address.replace(/https?:\/\/(?:www\.)?what3words\.com\/([a-z]+\.[a-z]+\.[a-z]+).*/i, "$1");
            }

        }

        if(this.params['first_name'] == null || this.params['first_name'].trim() === "") {
            this.presentAlert("Missing first name", "Please enter a first name.");
            validated = false;
        }
        else if(this.params['last_name'] == null || this.params['last_name'].trim() === "") {
            this.presentAlert("Missing last name", "Please enter a last name.");
            validated = false;
        }
        else if(this.params['country_id'] == null || this.params['country_id'].trim() === "") {
            this.presentAlert("Missing country", "Please select a country.");
            validated = false;
        }
        else {

            if(this.params['line_1'] == null || this.params['line_1'].trim() === "") {
                this.presentAlert("Missing line 1", "Please enter a Line 1.");
                validated = false;
            }
            else if(this.params['locality'] == null || this.params['locality'].trim() === "") {
                this.presentAlert("Missing town / city", "Please enter a town / city.");
                validated = false;
            }
            else if(this.address_requires_subdivision && (this.params['subdivision'] == null || this.params['subdivision'].trim() === "")) {
                this.presentAlert("Missing " + this.address_subdivision_label, "Please enter a " + this.address_subdivision_label + ".");
                validated = false;
            }
            else if(this.address_requires_postal_code && (this.params['postal_code'] == null || this.params['postal_code'].trim() === "")) {
                this.presentAlert("Missing " + this.address_postal_code_label, "Please enter a " + this.address_postal_code_label + ".");
                validated = false;
            }
            else if(parsed_what3words_address !== "" && !/^[a-z]+\.[a-z]+\.[a-z]+$/i.test(parsed_what3words_address)) {
                this.presentAlert("Invalid what3words address", "Please enter a valid what3words address.");
                validated = false;
            }
            else if(this.params['mobile_phone_number'] == null || this.params['mobile_phone_number'].trim() === "") {
                this.presentAlert("Missing mobile phone number", "Please enter a mobile phone number.");
                validated = false;
            }

        }

        if(validated) {

            if(parsed_what3words_address != null) {
                this.params['what3words_address'] = parsed_what3words_address;
            }

            this.addPersonShippingAddress().then(() => {
                this.modalController.dismiss();
            })

        }

    }

    addPersonShippingAddress() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Adding shipping address").then(async () => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.addPersonShippingAddress(this.person_id, this.params).then((shipping_address) => {
                        if(shipping_address) {
                            this.observerService.publish('person_shipping_address_added', shipping_address);
                        }
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to add a person shipping address.");
                        } 
                        else if(error.status == 403) {
                            this.presentAlert("Insufficient permissions", "You don't have permission to add a person shipping address.");
                        } 
                        else if(error.status == 404) {
                            this.presentAlert("Unavailable", "Unfortunately we're unable to add a person shipping address at this time.");
                        }
                        else if(error.status == 0) {
                            this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
